import React from 'react';
import {breakpointsMax, breakpointsMin} from '../../utils/breakpoints';
import styled from 'styled-components';
import {backgroundColor, BlockVerticalStyling, DynamicWidth, HeaderProps, showBackground} from '../types';
import {GatsbyImage} from 'gatsby-plugin-image';
import {Template} from '../../types/block';
import {baseCheckMarkBulletList} from '../../css/sharedStyle';

export const BackgroundColorWrapper = styled.div<backgroundColor>`
  width: 100%;
  background: ${props => {
    if (props.backgroundColor === 'Grey') {
      return '#F1F6F9';
    }
    if (props.backgroundColor === 'White') {
      return '#fff';
    }
  }};
`;

export const HoverWrapper = styled.div`
  .float-up {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &:hover .float-up {
    transform: translateY(-0.5em);
  }
`;

export const Header = styled.h2<HeaderProps>`
  font-weight: 400;
  width: 100%;
  text-align: ${props => props.textAlignMobile};
  font-style: normal;
  color: inherit;
  text-rendering: optimizeLegibility;
  margin: 0;
  font-size: 1.8rem;
  line-height: 1.4;
  @media ${breakpointsMin('tablet')} {
    text-align: ${props => props.textAlignDesktop};
    line-height: initial;
    margin-top: 1rem;
    width: ${props =>
      props.typename === 'DatoCmsOneColumnText' ||
      props.typename === 'DatoCmsBannerWithSignup' ||
      props.typename === 'DatoCmsHeadline' ||
      props.typename === 'DatoCmsBanner'
        ? '100%'
        : '85%'};
  }

  @media ${breakpointsMin('laptop')} {
    text-align: ${props => props.textAlignDesktop};
    font-size: 2.25rem;
    line-height: initial;
  }
`;

export const HeaderH1 = styled(Header).attrs({
  as: 'h1'
})`
  font-size: 2.5rem;
`;

export const BlueHeader = styled.span`
  color: #1493be;
  text-align: center;
  @media ${breakpointsMin('mobile')} {
    text-align: initial;
  }
`;

export const StyledImg = styled(props => <GatsbyImage fadeIn={false} {...props} />)<DynamicWidth>`
  max-width: 100%;
  width: ${props => props.dynwidthMobile}%;
  height: auto;
  margin: 3rem;

  @media ${breakpointsMin('mobile')} {
    margin: initial;
    width: ${props => props.dynwidth}%;
  }

  max-height: 400px;
  img {
    max-height: 400px;
    width: auto !important;
  }

  @media ${breakpointsMin('tablet')} {
    max-height: 600px;
    img {
      max-height: 600px;
    }
  }
`;

export const SingleImg = styled(props => <GatsbyImage {...props} />)`
  max-width: 100%;
  margin: 0;
  @media ${breakpointsMin('desktop')} {
    margin: 1rem 0;
    padding: initial;
    width: ${props => props.dynwidth}%;
  }
  @media ${breakpointsMin('desktop')} {
    max-width: 1280px;
    margin: 0 auto;
    padding: initial;
    width: ${props => props.dynwidth}%;
  }
`;

export const LinkSingleImg = styled(props => <GatsbyImage {...props} />)`
  max-width: 100%;
  width: ${props => props.dynwidthMobile}%;

  @media ${breakpointsMin('mobile')} {
    margin: 1rem;
    padding: initial;
    width: ${props => props.dynwidth}%;
  }

  @media ${breakpointsMin('desktop')} {
    margin: 1rem 0;
    padding: initial;
    width: ${props => props.dynwidth}%;
  }
`;

export const LinkSingleImgAnchor = styled.a<DynamicWidth>`
  display: block;
  max-width: 100%;
  width: ${props => props.dynwidthMobile}%;
  margin: 0 auto;
  padding: 1rem;
  @media ${breakpointsMin('mobile')} {
    padding: initial;
    width: ${props => props.dynwidth}%;
  }
`;

export const ColumnWrapper = styled.div<BlockVerticalStyling>`
  max-width: 1280px;
  display: flex;
  flex-flow: column;
  & > div:nth-child(1) {
    order: ${props => (props.typename === 'DatoCmsTwoColumnRichtextImage' ? 2 : 0)};
  }
  & > div:nth-child(2) {
    order: ${props => (props.typename === 'DatoCmsTwoColumnRichtextImage' ? 1 : 0)};
  }

  margin: 0 auto;
  padding-top: ${props => (props.previousBlockSameType ? '0' : '50px')};
  padding-bottom: 50px;
  & > div:nth-child(1) {
    margin-top: 0px;
  }
  @media ${breakpointsMin('mobile')} {
    & > div:nth-child(1) {
      order: ${props => (props.typename === 'DatoCmsTwoColumnRichtextImage' ? 1 : 0)};

      margin-top: ${props => (props.template === Template.BLOG_POST || props.typename === 'DatoCmsRichText' ? '0' : '45px')};
    }
    & > div:nth-child(2) {
      order: ${props => (props.typename === 'DatoCmsTwoColumnRichtextImage' ? 2 : 0)};
    }
    flex-flow: row;
    padding-top: ${props => (props.previousBlockSameType ? '0' : '50px')};
    margin-bottom: initial;
    padding-bottom: ${props => (props.nextBlockSameType ? '0' : '50px')};
    justify-content: center;
  }
`;

export const OneColumnTextWrapper = styled.div<showBackground>`
  background: ${props => {
    if (props.showBackground === 'Blue gradient') {
      return 'linear-gradient(to right, rgb(12, 50, 71) 0%, rgb(15, 87, 112) 60%, rgb(17, 92, 123) 100%);';
    }
    if (props.showBackground === 'Grey') {
      return '#F1F6F9';
    }
    if (props.showBackground === 'White') {
      return '#fff';
    }
  }};
`;

export const OneColumnText = styled.div<showBackground>`
  max-width: 1280px;
  display: block;
  margin: 0 auto;
  padding-top: 50px;
  padding-bottom: ${props => props.paddingBottom}px;
  text-align: center;
  padding-left: 1rem;
  padding-right: 1rem;
  color: ${props => {
    if (props.showBackground === 'Blue gradient') {
      return '#fff';
    }
    if (props.showBackground === 'Grey') {
      return '#000';
    }
    if (props.showBackground === 'White') {
      return '#000';
    }
  }};
  div p {
    margin-top: 2rem;
    margin-bottom: 0;
  }
  div > p > img {
    max-width: 100%;
  }
`;

export const StyledRichText = styled.div<{isBlogPost?: boolean}>`
  font-family: Open Sans, sans-serif !important;
  line-height: 1.8;

  p {
    line-height: 1.8;
  }

  h2 {
    margin-top: 2em;
  }

  /* done to correctly position element on click to scroll */
  h2:target,
  h3:target,
  h4:target,
  h5:target,
  h6:target {
    padding-top: 9.5rem;
    margin-top: -7rem;
  }

  h1,
  h2,
  h1 span,
  h2 span {
    font-weight: ${props => (props.isBlogPost ? '600 !important' : 'initial')};
  }

  h3,
  h4,
  h5,
  h6,
  h3 span,
  h4 span,
  h5 span,
  h6 span {
    font-weight: 600 !important;
    margin-bottom: 1em;
    margin-top: 2em;
  }

  img {
    height: initial !important;
    width: initial !important;
  }

  a {
    color: #1493be;
    pointer-events: auto;
    text-decoration: none;
    cursor: pointer;
  }

  .icon-checkmark {
    background-image: url('/images/icons/check.svg');
    background-repeat: no-repeat;
    width: 11px;
    height: 9px;
    display: block;
    margin: 15px auto;
    transform: scale(1.4);
  }
  .icon-crossed {
    background-image: url('/images/icons/cross.svg');
    background-repeat: no-repeat;
    width: 11px;
    height: 9px;
    display: block;
    margin: 15px auto;
    transform: scale(1.4);
    opacity: 0.5;
  }

  ${baseCheckMarkBulletList}

  ol li *,
  ol li:before {
    display: revert;
  }

  ul li.crossed:before {
    background-image: url('/images/icons/cross.svg') !important;
    opacity: 50%;
  }
  ul li.crossed {
    opacity: 25%;
  }
  ul li * {
    display: inline;
  }

  ol {
    padding-left: 1em;
  }
  ol li {
    line-height: 1.8;
    padding-bottom: 1em;
  }

  table {
    border: none !important;
    height: initial !important;

    ul {
      padding-left: 1rem;
      margin: 0 !important;
    }

    li {
      margin-left: -1em;
    }

    tr {
      border-radius: 0.6rem;
      height: initial !important;
    }

    tr:nth-child(even) {
    }

    tr:nth-child(odd) {
      background: #fff;
    }

    td {
      border: none;
      text-align: left;
      padding-left: 1em;
      height: initial !important;
    }

    td p {
      margin: 0;
    }

    @media ${breakpointsMin('tablet')} {
      td {
        font-size: 1em;
      }
      td p {
        margin: 0.5em 0;
      }
    }

    @media ${breakpointsMax('mobile')} {
      tr {
        display: flex !important;
        margin-bottom: 0;
      }
      td {
        width: 100%;
        width: fill-available;
        width: -moz-available;
        width: -webkit-fill-available;
        font-size: 0.5em;
        p {
          margin-bottom: 1.5em !important;
        }
      }
    }
  }
`;

export const RichText = styled(StyledRichText).attrs({
  className: 'richText'
})``;
